import React, { useState, useEffect, useMemo } from 'react'
import { Alert, withStyles } from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'
import { PatchContent, PatchData } from 'src/model/patch'
import { patchCollectionSettingsAction } from 'src/reducers/collection/action'
import UserType from 'src/model/user-types'
import { Typography } from '@material-ui/core'
import {
  WhiteLabelHeader,
  EmailProviderSelector,
  EmailSettings,
  UpgradeRedirect,
} from './components'
import styles from './styles'
import { Icon } from '@iconify/react'

const handleUserAgreement = () => {
  if (
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'Basic' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 1)' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 2)' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 3)' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 1A)' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 2A)' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 3A)'
  ) {
    return false
  }
  return true
}

const WhiteLabeling = ({ classes }) => {
  const dispatch = useDispatch()
  const collection = useSelector(state => state.collection.collection)
  const collectionSettings = useSelector(
    state => state.collection.collectionSettings,
  )

  const [selectedProvider, setSelectedProvider] = useState(
    collectionSettings?.emailProvider || 0,
  )
  const [apiToken, setApiToken] = useState(
    collectionSettings?.emailProviderApiKey || '',
  )
  const [sendingDomain, setSendingDomain] = useState(
    collectionSettings?.emailSenderAddress || '',
  )
  const [senderName, setSenderName] = useState(
    collectionSettings?.emailSenderName || '',
  )
  const [emailProviderEnabled, setEmailProviderEnabled] = useState(false)

  useEffect(() => {
    if (collectionSettings) {
      setSelectedProvider(collectionSettings.emailProvider || 0)
      setApiToken(collectionSettings.emailProviderApiKey || '')
      setSendingDomain(collectionSettings.emailSenderAddress || '')
      setSenderName(collectionSettings.emailSenderName || '')
      setEmailProviderEnabled(collectionSettings.emailProviderEnabled || false)
    }
  }, [collectionSettings])

  const handleProviderChange = event => {
    const newProvider = parseInt(event.target.value)
    setSelectedProvider(newProvider)
    // Clear fields when provider changes
    setApiToken('')
    setSendingDomain('')
    setSenderName('')
  }

  const isValidEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const isFormValid = useMemo(() => {
    // Check if any of the required fields are empty
    if (!apiToken || !sendingDomain || !senderName) return false

    // Validate each field
    const isApiTokenValid = apiToken.length >= 10
    const isSendingDomainValid = isValidEmail(sendingDomain)
    const isSenderNameValid = senderName.trim().length >= 2

    return isApiTokenValid && isSendingDomainValid && isSenderNameValid
  }, [apiToken, sendingDomain, senderName])

  const handleSaveSettings = () => {
    if (!isFormValid) return

    const patchSource = new PatchData(null, collection.id)
    patchSource.addContent(
      new PatchContent(
        selectedProvider,
        'emailProvider',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(
        apiToken,
        'emailProviderApiKey',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(
        sendingDomain,
        'emailSenderAddress',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(
        senderName,
        'emailSenderName',
        PatchData.OPERATION_REPLACE,
      ),
    )

    dispatch(
      patchCollectionSettingsAction(patchSource, () => {
        if (collectionSettings) {
          setSelectedProvider(collectionSettings.emailProvider || 0)
          setApiToken(collectionSettings.emailProviderApiKey || '')
          setSendingDomain(collectionSettings.emailSenderAddress || '')
          setSenderName(collectionSettings.emailSenderName || '')
          setEmailProviderEnabled(
            collectionSettings.emailProviderEnabled || false,
          )
        }
      }),
    )
  }

  const handleEmailProviderToggle = () => {
    const newState = !emailProviderEnabled
    setEmailProviderEnabled(newState)

    const patchSource = new PatchData(null, collection.id)
    patchSource.addContent(
      new PatchContent(
        newState,
        'emailProviderEnabled',
        PatchData.OPERATION_REPLACE,
      ),
    )

    dispatch(
      patchCollectionSettingsAction(patchSource, () => {
        if (collectionSettings) {
          setEmailProviderEnabled(
            collectionSettings.emailProviderEnabled || false,
          )
        }
        console.log('Email provider enabled state updated successfully')
      }),
    )
  }

  const userControl = handleUserAgreement()

  return (
    <div className={classes.container}>
      <WhiteLabelHeader
        userControl={userControl}
        emailProviderEnabled={emailProviderEnabled}
        onToggle={handleEmailProviderToggle}
      />

      {userControl ? (
        <div className={emailProviderEnabled ? '' : classes.disabledContent}>
          <EmailProviderSelector
            selectedProvider={selectedProvider}
            onProviderChange={handleProviderChange}
          />
          <EmailSettings
            provider={selectedProvider}
            apiToken={apiToken}
            onApiTokenChange={e => setApiToken(e.target.value)}
            sendingDomain={sendingDomain}
            onSendingDomainChange={e => setSendingDomain(e.target.value)}
            senderName={senderName}
            onSenderNameChange={e => setSenderName(e.target.value)}
            isValidEmail={isValidEmail}
            onSave={handleSaveSettings}
            disabled={!isFormValid}
          />
        </div>
      ) : (
        <UpgradeRedirect />
      )}
    </div>
  )
}

export default withStyles(styles)(WhiteLabeling)
